/**
 * Config Bridge
 * 
 * A singleton service that bridges the gap between React's ConfigContext
 * and pure TypeScript services that cannot use React hooks.
 * 
 * This ensures admin dashboard configurations are properly applied
 * throughout the entire codebase, including non-React services.
 */

import { GlobalConfig } from '../types';
import { listenToGlobalConfig, getGlobalConfig } from './firebase/dbService';

// ============== Types ==============

export interface ConfigBridgeState {
    globalConfig: GlobalConfig | null;
    isLoaded: boolean;
    lastUpdated: number | null;
    listeners: Set<ConfigListener>;
}

export type ConfigListener = (config: GlobalConfig | null) => void;

// ============== Default Config ==============

const DEFAULT_CONFIG: GlobalConfig = {
    freeDailyLimit: 10,
    freeAttachmentLimit: 5,
    premiumFileLimit: 20,
    freeModel: 'gemini-2.5-flash-lite',
    premiumModel: 'gemini-2.5-flash-lite',
    maxModel: 'gemini-2.5-flash-lite',
    proPrice: 9.99,
    maxPrice: 19.99,
    isSaleActive: false,
    saleDiscountPercent: 0,
    freeTokenLimit: 2000,
    premiumTokenLimit: 15000,
    maxTokenLimit: 50000,
    limitResetIntervalHours: 24,
    standardRefreshRate: 100,
    premiumRefreshRate: 500,
    maxRefreshRate: 1000,
    refreshIntervalMinutes: 60,
    apiKey: '',
    welcomeTitle: 'Welcome to Luca',
    welcomeMessage: 'Your AI-powered CPA reviewer',
    welcomeFeatures: [],
    extensionDiscounts: { months3: 10, months6: 15, months12: 25 },
    tokenWarningThreshold: 100,
    minBudgetPerRequest: 10,
    maxBudgetPerRequest: 1000,
    paypal: { clientId: '', environment: 'sandbox' },
};

// ============== Config Bridge Class ==============

class ConfigBridge {
    private state: ConfigBridgeState = {
        globalConfig: null,
        isLoaded: false,
        lastUpdated: null,
        listeners: new Set()
    };

    private unsubscribe: (() => void) | null = null;
    private initPromise: Promise<void> | null = null;

    /**
     * Initialize the config bridge
     * Should be called once at app startup
     */
    async init(): Promise<void> {
        if (this.initPromise) {
            return this.initPromise;
        }

        this.initPromise = this._initialize();
        return this.initPromise;
    }

    private async _initialize(): Promise<void> {
        try {
            // Load initial config
            const config = await getGlobalConfig();
            if (config) {
                // Force override premium model if it's the preview version
                if (config.premiumModel === 'gemini-2.5-flash-preview-05-20') {
                    config.premiumModel = 'gemini-2.5-flash-lite';
                }

                this.state.globalConfig = config;
                this.state.isLoaded = true;
                this.state.lastUpdated = Date.now();
                console.log('[ConfigBridge] Initial config loaded');
            } else {
                // Use defaults if no config in Firebase
                this.state.globalConfig = DEFAULT_CONFIG;
                this.state.isLoaded = true;
                console.log('[ConfigBridge] Using default config');
            }

            // Set up real-time listener
            this.unsubscribe = listenToGlobalConfig((config) => {
                if (config) {
                    // Force override premium model if it's the preview version
                    if (config.premiumModel === 'gemini-2.5-flash-preview-05-20') {
                        config.premiumModel = 'gemini-2.5-flash-lite';
                    }

                    const prevConfig = this.state.globalConfig;
                    this.state.globalConfig = config;
                    this.state.lastUpdated = Date.now();

                    // Notify listeners
                    this.notifyListeners(config);

                    // Log changes in development
                    if (process.env.NODE_ENV === 'development') {
                        console.log('[ConfigBridge] Config updated:', config);
                    }

                    // Dispatch global event for any listeners
                    if (typeof window !== 'undefined') {
                        window.dispatchEvent(new CustomEvent('configBridgeUpdated', {
                            detail: { config, prevConfig }
                        }));
                    }
                }
            });

            console.log('[ConfigBridge] Real-time listener established');
        } catch (error) {
            console.error('[ConfigBridge] Failed to initialize:', error);
            // Fall back to defaults
            this.state.globalConfig = DEFAULT_CONFIG;
            this.state.isLoaded = true;
        }
    }

    /**
     * Get the current global config synchronously
     * Returns null if not yet loaded, use getConfigAsync for guaranteed value
     */
    getConfig(): GlobalConfig | null {
        return this.state.globalConfig;
    }

    /**
     * Get config with defaults applied
     * Always returns a valid config object
     */
    getConfigWithDefaults(): GlobalConfig {
        return {
            ...DEFAULT_CONFIG,
            ...this.state.globalConfig
        };
    }

    /**
     * Get config asynchronously (waits for init if needed)
     */
    async getConfigAsync(): Promise<GlobalConfig> {
        if (!this.state.isLoaded) {
            await this.init();
        }
        return this.getConfigWithDefaults();
    }

    /**
     * Get a specific config value
     */
    get<K extends keyof GlobalConfig>(key: K): GlobalConfig[K] {
        const config = this.getConfigWithDefaults();
        return config[key];
    }

    /**
     * Check if config has been loaded
     */
    isLoaded(): boolean {
        return this.state.isLoaded;
    }

    /**
     * Get last update timestamp
     */
    getLastUpdated(): number | null {
        return this.state.lastUpdated;
    }

    /**
     * Subscribe to config changes
     */
    subscribe(listener: ConfigListener): () => void {
        this.state.listeners.add(listener);

        // Immediately call with current config if loaded
        if (this.state.isLoaded && this.state.globalConfig) {
            listener(this.state.globalConfig);
        }

        // Return unsubscribe function
        return () => {
            this.state.listeners.delete(listener);
        };
    }

    /**
     * Notify all listeners of config change
     */
    private notifyListeners(config: GlobalConfig): void {
        this.state.listeners.forEach(listener => {
            try {
                listener(config);
            } catch (error) {
                console.error('[ConfigBridge] Listener error:', error);
            }
        });
    }

    /**
     * Force refresh config from Firebase
     */
    async refresh(): Promise<GlobalConfig> {
        const config = await getGlobalConfig();
        if (config) {
            this.state.globalConfig = config;
            this.state.lastUpdated = Date.now();
            this.notifyListeners(config);
        }
        return this.getConfigWithDefaults();
    }

    /**
     * Clean up subscriptions
     */
    destroy(): void {
        if (this.unsubscribe) {
            this.unsubscribe();
            this.unsubscribe = null;
        }
        this.state.listeners.clear();
        this.initPromise = null;
    }

    // ============== Convenience Getters ==============

    /**
     * Get the AI model for a user tier
     */
    getModelForTier(tier: 'standard' | 'premium' | 'max'): string {
        const config = this.getConfigWithDefaults();
        switch (tier) {
            case 'max':
                return config.maxModel || config.premiumModel || 'gemini-2.5-flash-lite';
            case 'premium':
                return config.premiumModel || 'gemini-2.5-flash-lite';
            default:
                return config.freeModel || 'gemini-2.5-flash-lite';
        }
    }

    /**
     * Get token limit for a user tier
     */
    getTokenLimitForTier(tier: 'standard' | 'premium' | 'max'): number {
        const config = this.getConfigWithDefaults();
        switch (tier) {
            case 'max':
                return config.maxTokenLimit || 50000;
            case 'premium':
                return config.premiumTokenLimit || 15000;
            default:
                return config.freeTokenLimit || 2000;
        }
    }

    /**
     * Get token refresh rate for a user tier
     */
    getRefreshRateForTier(tier: 'standard' | 'premium' | 'max'): number {
        const config = this.getConfigWithDefaults();
        switch (tier) {
            case 'max':
                return config.maxRefreshRate || 1000;
            case 'premium':
                return config.premiumRefreshRate || 500;
            default:
                return config.standardRefreshRate || 100;
        }
    }

    /**
     * Check if a sale is currently active
     */
    isSaleActive(): boolean {
        return this.getConfigWithDefaults().isSaleActive || false;
    }

    /**
     * Get current sale discount percentage
     */
    getSaleDiscount(): number {
        const config = this.getConfigWithDefaults();
        return config.isSaleActive ? (config.saleDiscountPercent || 0) : 0;
    }
}

// ============== Singleton Export ==============

export const configBridge = new ConfigBridge();

// ============== Auto-Initialize ==============

// Initialize on module load (for services that import early)
if (typeof window !== 'undefined') {
    // Browser environment - initialize when DOM ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            configBridge.init().catch(console.error);
        });
    } else {
        configBridge.init().catch(console.error);
    }
}

// ============== Convenience Functions ==============

/**
 * Get current config (synchronous)
 */
export function getGlobalConfigSync(): GlobalConfig {
    return configBridge.getConfigWithDefaults();
}

/**
 * Get AI model for user
 */
export function getModelForUser(user: { isPremium?: boolean; isMax?: boolean } | null): string {
    if (!user) return configBridge.getModelForTier('standard');
    if (user.isMax) return configBridge.getModelForTier('max');
    if (user.isPremium) return configBridge.getModelForTier('premium');
    return configBridge.getModelForTier('standard');
}

/**
 * Get token limit for user
 */
export function getTokenLimitForUser(user: { isPremium?: boolean; isMax?: boolean } | null): number {
    if (!user) return configBridge.getTokenLimitForTier('standard');
    if (user.isMax) return configBridge.getTokenLimitForTier('max');
    if (user.isPremium) return configBridge.getTokenLimitForTier('premium');
    return configBridge.getTokenLimitForTier('standard');
}
